import React, { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Copy, Dices } from "lucide-react";

const genPassword = () => "GD-" + Math.random().toString(36).slice(2, 10);

function CredentialResult({ email, password }) {
  const copy = () => {
    navigator.clipboard.writeText(`Email: ${email}\nPassword: ${password}`);
    toast.success("Copied to clipboard");
  };
  return (
    <div className="bg-white/6 border border-white/15 rounded-md p-4 space-y-2" data-testid="credential-result">
      <div className="text-[10px] font-mono uppercase tracking-widest text-[hsl(var(--accent))]">Login details — save them now</div>
      <div className="text-sm">Email: <span className="font-mono">{email}</span></div>
      <div className="text-sm">Password: <span className="font-mono font-semibold" data-testid="credential-password">{password}</span></div>
      <Button size="sm" variant="outline" onClick={copy} className="border-white/20 text-white bg-transparent hover:bg-white/10" data-testid="copy-credentials">
        <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy
      </Button>
      <p className="text-xs text-white/40">Passwords are stored encrypted — this is the only time it can be shown.</p>
    </div>
  );
}

function PasswordField({ value, onChange, testid }) {
  return (
    <div>
      <Label className="text-xs text-white/60">Password (leave empty to auto-generate)</Label>
      <div className="flex gap-2 mt-1">
        <Input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
          className="bg-white/5 border-white/15 text-white font-mono" placeholder="auto-generate" />
        <Button type="button" variant="outline" onClick={() => onChange(genPassword())} title="Generate"
          className="border-white/20 text-white bg-transparent hover:bg-white/10 shrink-0" data-testid={`${testid}-generate`}>
          <Dices className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

export function NewUserDialog({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const create = async () => {
    if (name.trim().length < 2 || !email.trim()) { toast.error("Name and email are required"); return; }
    setBusy(true);
    try {
      const r = await api.post("/admin/users", {
        name: name.trim(), email: email.trim(),
        password: password.trim() || null, send_email: sendEmail,
      });
      setResult(r.data);
      onCreated();
      if (sendEmail) {
        toast[r.data.email_sent ? "success" : "warning"](
          r.data.email_sent ? `Login details emailed to ${r.data.email}` : "User created, but the email failed — share the password manually"
        );
      } else {
        toast.success("User created");
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(Array.isArray(d) ? d[0]?.msg || "Validation error" : d || "Create failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#171512] border-white/10 text-[#F5F1EA] max-w-md" data-testid="new-user-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl">New user</DialogTitle>
          <DialogDescription className="text-white/50 text-xs">
            Creates an account instantly — no admission application needed.
          </DialogDescription>
        </DialogHeader>
        {result ? (
          <>
            <CredentialResult email={result.email} password={result.password} />
            <Button onClick={onClose} className="w-full bg-[hsl(var(--accent))] text-black hover:bg-[hsl(var(--accent))]/90" data-testid="new-user-done">Done</Button>
          </>
        ) : (
          <div className="space-y-4">
            <div>
              <Label className="text-xs text-white/60">Full name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="new-user-name" className="bg-white/5 border-white/15 text-white mt-1" />
            </div>
            <div>
              <Label className="text-xs text-white/60">Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="new-user-email" className="bg-white/5 border-white/15 text-white mt-1" />
            </div>
            <PasswordField value={password} onChange={setPassword} testid="new-user-password" />
            <div className="flex items-center gap-2">
              <Checkbox id="send-mail" checked={sendEmail} onCheckedChange={(v) => setSendEmail(Boolean(v))} data-testid="new-user-send-email" />
              <Label htmlFor="send-mail" className="font-normal text-sm text-white/80">Email login details to the student</Label>
            </div>
            <Button onClick={create} disabled={busy} className="w-full bg-[hsl(var(--accent))] text-black hover:bg-[hsl(var(--accent))]/90" data-testid="new-user-create">
              {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Create user
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function ResetPasswordDialog({ user, onClose }) {
  const [password, setPassword] = useState("");
  const [sendEmail, setSendEmail] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const reset = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/users/${user.id}/reset-password`, {
        password: password.trim() || null, send_email: sendEmail,
      });
      setResult(r.data);
      toast.success("Password reset");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(Array.isArray(d) ? d[0]?.msg || "Validation error" : d || "Reset failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#171512] border-white/10 text-[#F5F1EA] max-w-md" data-testid="reset-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl">Reset password</DialogTitle>
          <DialogDescription className="text-white/50 text-xs">
            {user.name} · {user.email}. Sets a brand-new password — the old one cannot be viewed (stored encrypted).
          </DialogDescription>
        </DialogHeader>
        {result ? (
          <>
            <CredentialResult email={user.email} password={result.password} />
            <Button onClick={onClose} className="w-full bg-[hsl(var(--accent))] text-black hover:bg-[hsl(var(--accent))]/90" data-testid="reset-done">Done</Button>
          </>
        ) : (
          <div className="space-y-4">
            <PasswordField value={password} onChange={setPassword} testid="reset-password-input" />
            <div className="flex items-center gap-2">
              <Checkbox id="reset-mail" checked={sendEmail} onCheckedChange={(v) => setSendEmail(Boolean(v))} data-testid="reset-send-email" />
              <Label htmlFor="reset-mail" className="font-normal text-sm text-white/80">Email the new password to the student</Label>
            </div>
            <Button onClick={reset} disabled={busy} className="w-full bg-[hsl(var(--accent))] text-black hover:bg-[hsl(var(--accent))]/90" data-testid="reset-submit">
              {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Reset password
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
